if (!customElements.get("plyr-video")) {
  class PlyrVideo extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this.isExternalVideo = this.dataset.plyrProvider && this.dataset.plyrEmbedId;
    }

    connectedCallback() {
      if (this.initialized || this.classList.contains("plyr-initialized")) return;

      if (!this.isExternalVideo) {
        this.createVideoElement();
      } else {
        this.createExternalVideoContainer();
      }
      if (typeof window.initLazyObserver !== "undefined") {
        window.initLazyObserver(this, () => {
          this.initPlyrPlayer();
        });
      } else {
        this.initPlyrPlayer();
      }
    }

    createVideoElement() {
      const existingContent = this.innerHTML;
      this.innerHTML = "";
      
      this.videoElement = document.createElement("video");

      const booleanAttributes = ['controls', 'autoplay', 'loop', 'muted', 'playsinline'];
      booleanAttributes.forEach(attr => {
        if (this.dataset[attr] !== undefined) {
          if (attr === 'playsinline') {
            this.videoElement.playsInline = true;
          } else {
            this.videoElement[attr] = true;
          }
        }
      });
      
      if (this.dataset.lazyPoster) {
        this.videoElement.poster = this.dataset.lazyPoster;
      }
      
      this.appendChild(this.videoElement);
      
      if (existingContent) {
        this.videoElement.innerHTML = existingContent;
      }
    }

    createExternalVideoContainer() {
      this.innerHTML = "";
      this.setAttribute("data-plyr-provider", this.dataset.plyrProvider);
      this.setAttribute("data-plyr-embed-id", this.dataset.plyrEmbedId);
    }

    initPlyrPlayer() {
      if (this.initialized || this.classList.contains("plyr-initialized")) return;

      this.initialized = true;
      this.classList.add("plyr-initialized");

      if (!this.isExternalVideo) {
        this.initLazyAttributes();
      }
      this.loadAssetsAndInit();
    }

    initLazyAttributes() {
      if (this.dataset.lazyPoster) {
        this.videoElement.poster = this.dataset.lazyPoster;
        delete this.dataset.lazyPoster;
      }

      if (this.dataset.lazySrc) {
        this.videoElement.src = this.dataset.lazySrc;
        delete this.dataset.lazySrc;
      }

      this.querySelectorAll("source[data-src]").forEach((source) => {
        const newSource = document.createElement("source");
        newSource.src = source.dataset.src;
        newSource.type = source.type || "";
        this.videoElement.appendChild(newSource);
        source.remove();
      });

      if (this.videoElement.querySelectorAll("source").length > 0) {
        this.videoElement.load();
      }
    }

    loadAssetsAndInit() {
      const config = this.parseConfig();

      if (typeof Plyr !== "undefined") {
        this.initPlyr(config);
      } else {

        Promise.all([
          window.loadByUrl(window?.theme?.static?.plyrCss, () => {}, { type: 'css', check: 'plyrcss' }),
          new Promise((resolve, reject) => {
            window.loadByUrl(window?.theme?.static?.plyrJs, () => {
              if (typeof window.Plyr === 'function') {
                resolve();
              } else {
                reject(new Error('Plyr failed to load or is not a constructor'));
              }
            }, { type: 'js', check: 'Plyr' });
          })
        ]).then(() => {
          this.initPlyr(config);
        }).catch(err => {
          console.error('Plyr load fail:', err);
        });
      }

      if (this.dataset.hls) {
        this.initHls(config);
      }
    }

    parseConfig() {
      let config = {};
      if (this.dataset.plyrConfig) {
        try {
          const customConfig = JSON.parse(this.dataset.plyrConfig);
          config = { ...config, ...customConfig };
        } catch (e) {
          console.warn("Invalid Plyr config in data-plyr-config:", this.dataset.plyrConfig);
        }
      }

      if (config.autoplay) {
        config.autoplay = true;

        if (config.muted !== false) {
          config.muted = true;
        }
      }

      return config;
    }

    initPlyr(config) {
      const targetElement = this.isExternalVideo ? this : this.videoElement;
      const player = new Plyr(targetElement, config);

      if ((config.autoplay || this.dataset.autoplay) && player) {
        this.handleChromeAutoplayPolicy(player);
        player.play();
        player.muted = true;
      }

      const card = this.closest('.card');
      const cardOverlay = card?.querySelector('.card-img-overlay');
      const playBtn = cardOverlay?.querySelector('.play-btn');
      if (!cardOverlay || !playBtn) return;

      playBtn.removeEventListener('click', this._handlePlayClick);
      const handlePlayClick = (e) => {
        e.preventDefault();
        player.play()
          .then(() => {
            cardOverlay.classList.add('d-none');
          })
          .catch((err) => {
            console.warn('Play fail:', err);
          });
      };
      playBtn.addEventListener('click', handlePlayClick);
      this._handlePlayClick = handlePlayClick;

      player.on('pause', () => {
        cardOverlay.classList.remove('d-none');
      });
      player.on('ended', () => {
        cardOverlay.classList.remove('d-none');
      });
    }

    handleChromeAutoplayPolicy(player) {
      if (this.isExternalVideo && this.dataset.plyrProvider === "youtube") {
        setTimeout(() => {
          try {
            player.muted = true;
            const playPromise = player.play();
            if (playPromise instanceof Promise) {
              playPromise.catch(() => {
                console.log("YouTube autoplay blocked, user interaction required");
              });
            }
          } catch (error) {
            console.log("YouTube autoplay failed:", error);
          }
        }, 1000);

        return;
      }

      const autoPlayVideo = () => {
        try {
          const playPromise = player.play();
          if (playPromise instanceof Promise) {
            playPromise.catch(() => {
              player.muted = true;
              const retryPromise = player.play();
              if (retryPromise instanceof Promise) {
                retryPromise.catch(() => {});
              }
            });
          }
        } catch (error) {
          console.warn("Autoplay failed:", error);
        }

        document.removeEventListener("click", autoPlayVideo);
        document.removeEventListener("touchstart", autoPlayVideo);
      };

      document.addEventListener("click", autoPlayVideo, { once: true });
      document.addEventListener("touchstart", autoPlayVideo, { once: true });
    }

    initHls(config) {
      let source = null;

      if (this.dataset.lazySrc) {
        source = this.dataset.lazySrc;
        delete this.dataset.lazySrc;
      }

      if (!source) {
        const sourceElement = this.videoElement.querySelector("source");
        if (sourceElement) {
          source = sourceElement.src || sourceElement.dataset.src;
        }
      }

      if (!source) {
        console.error("No HLS source found");
        return;
      }

      const hlsJsUrl = window.hlsJsUrl || window.theme?.hls?.js;
      if (typeof Hls === "undefined") {
        window.loadByUrl(hlsJsUrl, () => {
          this.attachHls(source);
        },{ type: "js", check: "Hls" });
      } else {
        this.attachHls(source);
      }
    }

    attachHls(source) {
      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(source);
        hls.attachMedia(this.videoElement);
      } else if ( this.videoElement.canPlayType("application/vnd.apple.mpegurl") ) {
        this.videoElement.src = source;
      } else {
        console.error("HLS not supported");
      }
    }
  }
  customElements.define("plyr-video", PlyrVideo);
}
